
interface InstallmentCellDelegate
{
    installmentCellDidRequestReview( cell:InstallmentCell, installment:AdyenProxyPaymentInstallment ):void;
    installmentCellDidChangeSession( cell:InstallmentCell, sessionID:string ):void;
}

class InstallmentCell extends UITableViewCell
{
    delegate:InstallmentCellDelegate = null;

    /// The issues of the session of an installment held for review, when
    /// loaded. Set before the item.
    issues:PaymentSessionIssues = null;

    private docNumberLabel:MUILabel = null;    
    private dateLabel:MUILabel = null;
    private conceptLabel:MUILabel = null;
    private referenceLabel:MUILabel = null;
    private amountLabel:MUILabel = null;
    private paymentMethodButton:MUIButton = null;
    private paymentMethodLabel:MUILabel = null;
    private paymentStatusLabel:MUILabel = null;
    private optionsButton:MUIButton = null;
    private issueButton:MUIButton = null;

    awakeFromHTML(): void {
        this.docNumberLabel = MUIOutlet( this, "doc-number-lbl", "MUILabel" );
        this.dateLabel = MUIOutlet( this, "date-lbl", "MUILabel" );
        this.conceptLabel = MUIOutlet( this, "concept-lbl", "MUILabel" );
        this.referenceLabel = MUIOutlet( this, "concept-sub-lbl", "MUILabel" );
        this.amountLabel = MUIOutlet( this, "amount-lbl", "MUILabel" );
        this.paymentMethodLabel = MUIOutlet( this, "pay-method-lbl", "MUILabel" );
        this.paymentMethodButton = MUIOutlet( this, "pay-method-btn", "MUIButton" );        
        this.paymentMethodButton.setAction( this, function(this:InstallmentCell) {
            AppHelper.sharedInstance().showSelectPaymethodViewControllerFromView( this.paymentMethodButton, this.installment.paymentMethod, this, function(this:InstallmentCell, controller:any, object:PayMethod){
                this.installment.paymentMethod = object;
                this.paymentMethodButton.title = object?.name;
            });
        });
        this.paymentStatusLabel = MUIOutlet( this, "payment-status-lbl", "MUILabel" );
        this.issueButton = MUIOutlet( this, "issue-btn", "MUIButton" );
        this.issueButton.setAction( this, function(this:InstallmentCell){
            this.showSessionTransactions();
        });
        this.optionsButton = MUIOutlet( this, "options-btn", "MUIButton" );
        this.optionsButton.setAction(this, this.showOptions);

        // A click on an installment held for review opens its transactions: no selected row
        this.selectionStyle = UITableViewCellSelectionStyle.None;
    }

    /// A Dual-Link Payments installment held for review: its session's
    /// transactions have issues to review.
    static isReviewable( item:PaymentInstallment ) : boolean {
        return item instanceof AdyenProxyPaymentInstallment
            && item.status == PaymentInstallmentStatus.review
            && item.sessionID != null;
    }

    /// The installment that OWNS the session behind a row: the row itself,
    /// or - on a split chain ( voided total -> bank part -> pocket ) - the
    /// linked part that carries the sessionID. One hop covers all three.
    static sessionInstallmentOf( item:PaymentInstallment ) : AdyenProxyPaymentInstallment {
        if (item instanceof AdyenProxyPaymentInstallment && item.sessionID != null) return item;
        if (item.previous instanceof AdyenProxyPaymentInstallment && item.previous.sessionID != null) return item.previous;
        if (item.next instanceof AdyenProxyPaymentInstallment && item.next.sessionID != null) return item.next;
        return null;
    }

    /// Dual Link owns these rows: the Dual-Link Payments payouts and the
    /// DUAL LINK TRANSFER pockets of a split. The venue sets nothing on them
    /// by hand: no status, no invoice, no operation id.
    static isDualLink( item:PaymentInstallment ) : boolean {
        return item instanceof ProxyPaymentInstallment;
    }

    /// "Collected 29/09", or "Paid 29/09" for the installments we pay
    static paidStatusString( installment:PaymentInstallment ) : string {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let date = installment.paymentDate != null ? ad.dateFormatter.stringFromDate( installment.paymentDate ).substring( 0, 5 ) : "";
        let outgoing = installment instanceof SupplierPaymentInstallment || ( installment.amount ?? 0 ) < 0;
        let format = outgoing ? MIOLocalizeString( "PAID_ON_DATE", "PAID %s" ) : MIOLocalizeString( "COLLECTED_ON_DATE", "COLLECTED %s" );
        return format.replace( "%s", date ).trim();
    }

    private static styles = [ "alert", "warning", "caution", "green" ];

    private labels() : MUILabel[] {
        return [ this.docNumberLabel, this.dateLabel, this.conceptLabel, this.referenceLabel, this.amountLabel, this.paymentMethodLabel, this.paymentStatusLabel ];
    }

    private add_style(style:string) {
        for (let label of this.labels()) MUICoreLayerAddStyle( label.layer, style );
    }

    // The cell updates in place after its options: no style of the previous status stays
    private clear_styles() {
        for (let label of this.labels()) {
            for (let style of InstallmentCell.styles) MUICoreLayerRemoveStyle( label.layer, style );
        }
        MUICoreLayerRemoveStyle( this.paymentStatusLabel.layer, "pi-strong" );
    }

    /// The last installment of a due date closes its card
    set isLastInSection( value:boolean ) {
        if (value) MUICoreLayerAddStyle( this.layer, "pi-last" );
        else MUICoreLayerRemoveStyle( this.layer, "pi-last" );
    }

    showsDueDate = false;

    private installment:PaymentInstallment = null;
    set item( item: PaymentInstallment ) {
        this.installment = item;

        let escape = ReconciliationEntryStateHelper.escape;
        this.docNumberLabel.text = (item instanceof LegalEntityPaymentInstallment) ? escape( item.documentNumber ) : null;
        
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        // Sorting by date groups the cards by the session day: the cell's
        // date column shows the DUE date then, so both stay readable.
        this.dateLabel.text = ad.dateFormatter.stringFromDate( this.showsDueDate ? ( item.dueDate ?? item.date ) : item.date );
        this.conceptLabel.text = escape( item.concept );
        this.referenceLabel.text = escape( item.operationReference );
        this.referenceLabel.hidden = item.operationReference == null || item.operationReference.length == 0;
        this.amountLabel.text = ad.currencyFormatter.stringFromNumber( item.amount );

        // Split chain ( eg. a held payout resolved into bank + pocket ): tie
        // the linked rows visually through the sub label.
        if (item.next != null) {
            this.referenceLabel.text = "\u2937 " + MIOLocalizeString("SPLIT_INTO", "SPLIT INTO") + " " + escape( item.next.concept ) + " (" + ad.currencyFormatter.stringFromNumber( item.next.amount ) + ")";
            this.referenceLabel.hidden = false;
        }
        else if (item.previous != null) {
            this.referenceLabel.text = "\u2936 " + escape( item.previous.concept ) + " (" + ad.currencyFormatter.stringFromNumber( item.previous.amount ) + ")";
            this.referenceLabel.hidden = false;
        }

        this.updatePaymentStatus();
    }

    private updatePaymentStatus() {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        this.clear_styles();
        this.paymentStatusLabel.text = this.installment.statusString();

        // Reset reuse state before the status cases decide which one shows
        this.paymentMethodButton.hidden = false;
        this.paymentMethodLabel.hidden = false;

        switch(this.installment.status) {

            case PaymentInstallmentStatus.invalidAmount:
                this.paymentMethodLabel.text = this.installment.paymentMethod?.name;
                this.paymentMethodButton.hidden = true;
                this.add_style("alert");
            break;    

            case PaymentInstallmentStatus.pending:
                this.paymentMethodButton.title = this.installment.paymentMethod?.name;
                this.paymentMethodLabel.hidden = true;
            break;
    
            case PaymentInstallmentStatus.fileGenerated:
            case PaymentInstallmentStatus.locked:            
                this.paymentMethodLabel.text = this.installment.paymentMethod?.name;
                this.paymentMethodButton.hidden = true;
                this.add_style( "warning" );
            break;

            case PaymentInstallmentStatus.paid:
                this.paymentMethodLabel.text = this.installment.paymentMethod?.name;
                this.paymentMethodButton.hidden = true;
                this.paymentStatusLabel.text = InstallmentCell.paidStatusString( this.installment );
                MUICoreLayerAddStyle( this.paymentStatusLabel.layer, "green" );
            break;

            case PaymentInstallmentStatus.compensated:
            case PaymentInstallmentStatus.partialPayment:
                this.paymentMethodLabel.text = this.installment.paymentMethod?.name;
                this.paymentMethodButton.hidden = true;
                if (this.installment.paymentDate != null) this.paymentStatusLabel.text += " " + ad.dateFormatter.stringFromDate( this.installment.paymentDate ).substring( 0, 5 );
                MUICoreLayerAddStyle( this.paymentStatusLabel.layer, "green" );
            break;

            case PaymentInstallmentStatus.returned:
                this.paymentMethodLabel.text = this.installment.paymentMethod?.name;
                this.paymentMethodButton.hidden = true;
                this.add_style( "alert" );
            break;

            case PaymentInstallmentStatus.void:
                this.paymentMethodLabel.text = this.installment.paymentMethod?.name;
                this.paymentMethodButton.hidden = true;
                this.docNumberLabel.text = this.strike_through( this.docNumberLabel.text );
                this.dateLabel.text = this.strike_through( this.dateLabel.text );
                this.conceptLabel.text = this.strike_through( this.conceptLabel.text );
                this.referenceLabel.text = this.strike_through( this.referenceLabel.text );
                this.amountLabel.text = this.strike_through( this.amountLabel.text );
                this.paymentMethodLabel.text = this.strike_through( this.paymentMethodLabel.text );
                this.paymentStatusLabel.text = this.strike_through( this.paymentStatusLabel.text );
            break;

            case PaymentInstallmentStatus.confirmedButNotPay:
                this.paymentMethodLabel.text = this.installment.paymentMethod?.name;
                this.paymentMethodButton.hidden = true;
                this.add_style( "caution" );
            break;

            case PaymentInstallmentStatus.doubtful:
                this.paymentMethodLabel.text = this.installment.paymentMethod?.name;
                this.paymentMethodButton.hidden = true;
                this.add_style( "alert" );
            break;

            case PaymentInstallmentStatus.creditNote:
                this.paymentMethodLabel.text = this.installment.paymentMethod?.name;
                this.paymentMethodButton.hidden = true;
                this.add_style( "alert" );
            break;

            // Held for manual review: the session's venue lines do not
            // reconcile with the registered payments. The whole row in red,
            // its status in bold; the issues triangle opens the transactions.
            case PaymentInstallmentStatus.review:
                this.paymentMethodLabel.text = this.installment.paymentMethod?.name;
                this.paymentMethodButton.hidden = true;
                this.add_style( "alert" );
                MUICoreLayerAddStyle( this.paymentStatusLabel.layer, "pi-strong" );
            break;

        }

        // Red while some issue has no action yet ( or before they load ),
        // yellow once all of them have one. The rows of a resolved split
        // ( the voided total, the bank part, the pocket ) keep the icon in
        // its reviewed state: the session's story stays one click away.
        let reviewable = InstallmentCell.isReviewable( this.installment );
        // Only the ROOT of a resolved split keeps the icon: the parts below
        // it ARE the resolution.
        let chained = !reviewable
                   && this.installment.previous == null
                   && this.installment.next != null
                   && InstallmentCell.sessionInstallmentOf( this.installment ) != null;
        let reviewed = chained || ( reviewable && this.issues != null && this.issues.issues > 0 && this.issues.pending == 0 );
        this.issueButton.hidden = !( reviewable || chained );
        if (reviewed) MUICoreLayerAddStyle( this.issueButton.layer, "reviewed" );
        else MUICoreLayerRemoveStyle( this.issueButton.layer, "reviewed" );
        this.issueButton.layer.title = reviewed ? MIOLocalizeString( "ISSUES_REVIEWED", "ISSUES REVIEWED" ) : MIOLocalizeString( "ISSUES_TO_REVIEW", "ISSUES TO REVIEW" );

        if (reviewable || chained) MUICoreLayerAddStyle( this.layer, "pi-reviewable" );
        else MUICoreLayerRemoveStyle( this.layer, "pi-reviewable" );

        if (this.installment instanceof AdyenProxyPaymentInstallment) {
            // The payment method is fixed by the platform on proxy installments
            this.paymentMethodLabel.text = "Dual-Link Payments";
            this.paymentMethodLabel.hidden = false;
            this.paymentMethodButton.hidden = true;
        }

        // A Dual Link row with nothing to offer shows no gear
        this.optionsButton.hidden = InstallmentCell.isDualLink( this.installment ) && this.dualLinkActions().length == 0;
    }

    private strike_through(text:string) {
        return "<strike>" + text + "</strike>";
    }

    private showOptions(){
        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString( "OPTIONS", "OPTIONS" ), MIOLocalizeString("SELECT_AN_OPTION", "SELECT AN OPTION"), MUIAlertViewStyle.Default );

        let actions = InstallmentCell.isDualLink( this.installment ) ? this.dualLinkActions() : this.installmentActions();
        for (let action of actions) avc.addAction( action );

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null ) );

        AppHelper.sharedInstance().presentViewController( avc, true );
    }

    /// What a Dual Link row offers: its session's transactions, RE-CHECK
    /// while the payout is held for review, and TRANSFER while it is still
    /// pending. A transferred payout, or the pocket ( born paid ), keeps the
    /// transactions alone.
    private dualLinkActions() : MUIAlertAction[] {
        let actions:MUIAlertAction[] = [];
        let item = this.installment;

        if (InstallmentCell.sessionInstallmentOf( item ) != null) {
            actions.push( MUIAlertAction.alertActionWithTitle( MIOLocalizeString("SESSION_TRANSACTIONS", "SESSION TRANSACTIONS"), MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
                this.showSessionTransactions();
            } ));
        }

        if (item instanceof AdyenProxyPaymentInstallment && item.status == PaymentInstallmentStatus.review) {
            actions.push( MUIAlertAction.alertActionWithTitle( MIOLocalizeString("RE_CHECK_SESSION", "RE-CHECK SESSION"), MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
                this.recheckSession();
            } ));
        }

        // Only a pending payout moves money: the server refuses any other
        if (item instanceof AdyenProxyPaymentInstallment && item.status == PaymentInstallmentStatus.pending && item.paymentDate == null) {
            actions.push( MUIAlertAction.alertActionWithTitle( MIOLocalizeString("TRANSFER", "TRANSFER"), MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
                this.doPayment();
            } ));
        }

        return actions;
    }

    /// The actions of an installment the venue manages itself
    private installmentActions() : MUIAlertAction[] {
        let actions:MUIAlertAction[] = [];

        if ( MUIWebApplication.sharedInstance().delegate.selectedUser.isAdmin ) {

            actions.push( MUIAlertAction.alertActionWithTitle( "LINK WITH INVOICE", MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
                this.showInvoice();
            } ));

            actions.push( MUIAlertAction.alertActionWithTitle( "UNLINK INVOICE", MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
                this.unlinkInvoice();
            } ));

            actions.push( MUIAlertAction.alertActionWithTitle( "GENERATE INVOICE", MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
                this.generateInvoice();
            } ));

            actions.push( MUIAlertAction.alertActionWithTitle( "UPDATE INVOICE", MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
                this.updateInvoice();
            } ));

            actions.push( MUIAlertAction.alertActionWithTitle( "ASSIGN OPERATION ID", MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
                this.showSelectOperationIDViewController();
            } ));

            actions.push( MUIAlertAction.alertActionWithTitle( "REMOVE OPERATION ID", MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
                this.installment.operationReference = null;
            } ));

            // actions.push( MUIAlertAction.alertActionWithTitle( "DELETE", MUIAlertActionStyle.Destructive, this, function(this:InstallmentCell){
            //     DBHelper.deleteObjectFromMainContext( this.installment, true );
            // } ));
        }

        actions.push( MUIAlertAction.alertActionWithTitle( "MARK AS PENDING", MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
            this.installment.status = PaymentInstallmentStatus.pending;
            DBHelper.saveMainContext();
        } ));

        if (this.installment.status == PaymentInstallmentStatus.pending) {

            actions.push( MUIAlertAction.alertActionWithTitle( MIOLocalizeString("SPLIT_INSTALLMENT", "SPLIT INSTALLMENT"), MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
                this.showSplitCountAlert();
            } ));

            actions.push( MUIAlertAction.alertActionWithTitle( "MARK AS UNPAID", MUIAlertActionStyle.Destructive, this, function(this:InstallmentCell){
                this.installment.status = (PaymentInstallmentStatus as any).unpaid;
                DBHelper.saveMainContext();
            } ));

            actions.push( MUIAlertAction.alertActionWithTitle( "MARK AS CONFIRMED, NOT PAY", MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
                this.installment.status = PaymentInstallmentStatus.confirmedButNotPay;
                DBHelper.saveMainContext();
            } ));    
        }
        
        actions.push( MUIAlertAction.alertActionWithTitle( "MARK AS RETURNED", MUIAlertActionStyle.Destructive, this, function(this:InstallmentCell){
            this.installment.status = PaymentInstallmentStatus.returned;
            DBHelper.saveMainContext();
        } ));

        actions.push( MUIAlertAction.alertActionWithTitle( "MARK AS LOCK", MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
            this.installment.status = PaymentInstallmentStatus.locked;
            DBHelper.saveMainContext();
        } ));

        actions.push( MUIAlertAction.alertActionWithTitle( "MARK AS PAID", MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
            this.showPaymentAmountAlert( PaymentInstallmentStatus.paid);
        } ));

        actions.push( MUIAlertAction.alertActionWithTitle( "MARK AS COMPENSATED", MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
            this.showPaymentAmountAlert( PaymentInstallmentStatus.compensated);
        } ));

        actions.push( MUIAlertAction.alertActionWithTitle( "VOID INVOICE", MUIAlertActionStyle.Destructive, this, function(this:InstallmentCell){
            this.voidInvoice();
        } ));

        if (this.installment.status == PaymentInstallmentStatus.doubtful || this.installment.status == PaymentInstallmentStatus.returned) {
            actions.push( MUIAlertAction.alertActionWithTitle( "GENERATE RECLAIM INSTALLMENT", MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
                this.showReclaimInstallmentAlert();
            } ));

            actions.push( MUIAlertAction.alertActionWithTitle( "CREATE RECLAIM INVOICE", MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
                this.showReclaimInvoiceAlert();
            } ));
        }

        // TODO: Check for SEPA account and enable this option
        if (this.installment.status == PaymentInstallmentStatus.pending && this.installment.paymentMethod?.type != PayMethodType.AdyenBalance) {
            actions.push( MUIAlertAction.alertActionWithTitle( "GENERATE SEPA FILE", MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
                this.showSEPAOptionsAlert();
            } ));

            actions.push( MUIAlertAction.alertActionWithTitle( "DELETE", MUIAlertActionStyle.Destructive, this, function(this:InstallmentCell){
                DBHelper.deleteObjectFromMainContext(this.installment, true);
            } ));
        }

        if (this.installment.status == PaymentInstallmentStatus.pending && this.installment.paymentMethod?.type == PayMethodType.AdyenBalance) {
            actions.push( MUIAlertAction.alertActionWithTitle( "DO THE PAYMENT", MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
                this.doPayment();
            } ));
        }

        return actions;
    }

    private showInvoice(){
        let avc = new MUIAlertViewController();
        avc.initWithTitle( MIOLocalizeString( "INVOICE", "INVOICE" ), MIOLocalizeString("TYPE_THE_NUMBER_OF_THE_INVOICE", "TYPE THE NUMBER OF THE INVOICE"), MUIAlertViewStyle.Default );

        avc.addTextFieldWithConfigurationHandler( this, function(this:InstallmentCell, textField:MUITextField){
            textField.placeholderText = MIOLocalizeString("DOCUMENT_NUMBER", "DOCUMENT NUMBER");
        });

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString( "OK", "OK" ), MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
            this.linkWithInvoice( avc.textFields[0].text );
        } ));

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        AppHelper.sharedInstance().presentViewController( avc, true );
    }

    private linkWithInvoice(documentNumber:string) {
        DBHelper.queryObjectWithCompletion( "SalesInvoice", null, MIOPredicate.predicateWithFormat( "documentNumber == '" + documentNumber + "'" ), [], this, function(object:SalesInvoice) {

            if (object == null) {
                AppHelper.showErrorMessage(null, "ERROR", "COULDN'T FOUND ANY INVOICE");
                return;
            }
            
            if (this.installment instanceof LegalEntityPaymentInstallment) {
                this.installment.invoice = object;
                this.installment.documentNumber = object.documentNumber;
                this.docNumberLabel.text = object.documentNumber;
            }
        } );
    }

    private unlinkInvoice() {
        
        if (this.installment instanceof LegalEntityPaymentInstallment) {
            this.installment.invoice = null;
            this.installment.documentNumber = null;
        }

        this.docNumberLabel.text = null;
    }

    private generateInvoice() {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        ad.webService.createInvoiceFromInstallment( this.installment, (response, error) => {
            if (error != null ) {
                AppHelper.showErrorMessage(null, MIOLocalizeString("ERROR","ERROR"), error );
                return;
            }            
            
            this.docNumberLabel.text = response["number"];
            AppHelper.showInfoMessage(null, MIOLocalizeString("INFO", "INFO"), MIOLocalizeString("THE_OPERATION_WAS_SUCCESSFULL", "THE OPERATION WAS SUCCESSFULL"));

            // let invoiceID = response["id"];
            // if ( invoiceID != null ) {                
            //     DBHelper.queryObjectFromMainContext( "SalesInvoice", MIOPredicate.predicateWithFormat( "identifier == " + invoiceID), [] );
            // }
            
        });
    }    

    private updateInvoice() {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        ad.webService.updateInvoiceFromInstallment( this.installment, (response, error) => {
            if (error != null ) {
                AppHelper.showErrorMessage(null, MIOLocalizeString( "ERROR", "ERROR" ), error );
                return;
            }            
            
            AppHelper.showInfoMessage(null, MIOLocalizeString("INFO", "INFO"), MIOLocalizeString("THE_OPERATION_WAS_SUCCESSFULL", "THE OPERATION WAS SUCCESSFULL"));
        });
    }

    private showSelectOperationIDViewController() {
        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString( "OPERATION", "OPERATION" ), MIOLocalizeString("TYPE_THE_OPERATION_ID", "TYPE THE OPERATION ID"), MUIAlertViewStyle.Default );

        avc.addTextFieldWithConfigurationHandler( this, function(this:InstallmentCell, textField:MUITextField){
            textField.placeholderText = MIOLocalizeString("OPERATION_ID", "OPERATION ID");
        });

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString( "OK", "OK" ), MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
            this.assignOperationRef( avc.textFields[0].text.trim() );
        } ));

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        AppHelper.sharedInstance().presentViewController( avc, true );
    }

    private assignOperationRef( ref: string ) {
        if (ref.length == 0 ) return;

        this.installment.operationReference = ref;
        DBHelper.saveMainContext();
    }

    private showPaymentAmountAlert( status:PaymentInstallmentStatus ){
        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString( "INFO", "INFO" ), MIOLocalizeString("INOUT_AMOUNT", "TYPE THE OPERATION ID"), MUIAlertViewStyle.Default );

        avc.addTextFieldWithConfigurationHandler( this, function(this:InstallmentCell, textField:MUITextField){
            textField.placeholderText = MIOLocalizeString("PAYMENT_DATE", "PAYMENT DATE");
        } );

        avc.addTextFieldWithConfigurationHandler( this, function(this:InstallmentCell, textField:MUITextField){
            textField.placeholderText = MIOLocalizeString( "COMMENTS", "COMMENTS" );
        } );    

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString( "OK", "OK" ), MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
            this.checkPaymentAmountString( status, avc.textFields[0].text.trim(), avc.textFields[1].text.trim());
        } ));

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        AppHelper.sharedInstance().presentViewController( avc, true );   
    }

    private voidInvoice() {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        if ( this.installment instanceof LegalEntityPaymentInstallment ) {

            ad.webService.nullifyInvoice( this.installment.invoice, (data:any, error:string) => {
                if (error == null) {
                    AppHelper.showInfoMessage( null, MIOLocalizeString("INFO", "INFO"), MIOLocalizeString("VOID_INVOICE_CREATED_SUCCESFULLY", "VOID INVOICE CREATED SUCCESFULLY") );
                    this.installment.status = PaymentInstallmentStatus.void;
                }
                else {
                    AppHelper.showErrorMessage( null, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString( "VOID INVOICE COULDN'T BE CREATED", "VOID INVOICE COULDN'T BE CREATED") );
                }
            });
        }    
    }   

    private showReclaimInvoiceAlert() {
        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString( "INFO", "INFO" ), MIOLocalizeString("RECLAIM_AMOUNT", "RECLAIM AMOUNT"), MUIAlertViewStyle.Default );

        avc.addTextFieldWithConfigurationHandler( this, function(this:InstallmentCell, textField:MUITextField){
            textField.placeholderText = MIOLocalizeString("NEW_INVOICE_AMOUNT", "NEW INVOICE AMOUNT");
        } );

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString( "OK", "OK" ), MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
            this.reclaimInvoice( avc.textFields[0].text.trim() );
        } ));

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString( "CANCEL", "CANCEL" ), MUIAlertActionStyle.Cancel, null, null));

        AppHelper.sharedInstance().presentViewController( avc, true );   
    }

    private reclaimInvoice(amount:string) {

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        let total = ad.currencyFormatter.numberFromString( amount );        

        if ( this.installment instanceof LegalEntityPaymentInstallment ) {
            ad.webService.createReclaimInvoice( this.installment.invoice, total, null);
        }
    } 

    private checkPaymentAmountString( status:PaymentInstallmentStatus, date:string, comments:string ) {

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;        
        let d = ad.dateFormatter.dateFromString( date );

        this.installment.status = status;
        this.installment.paymentDate = d;
        this.installment.comments = comments ?? "";
        
        DBHelper.saveMainContext();
    }

    private showReclaimInstallmentAlert() {

        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString( "INFO", "INFO" ), MIOLocalizeString("RECLAIM_OPTIONS", "RECLAIM OPTIONS"), MUIAlertViewStyle.Default );

        avc.addTextFieldWithConfigurationHandler( this, function(this:InstallmentCell, textField:MUITextField){
            textField.placeholderText = MIOLocalizeString("NEW_DATE", "NEW DATE");
        } );

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString( "OK", "OK" ), MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
            this.reclaimInstallment( avc.textFields[0].text.trim() );
        } ));

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString( "CANCEL", "CANCEL" ), MUIAlertActionStyle.Cancel, null, null) );

        AppHelper.sharedInstance().presentViewController( avc, true );  
    } 


    private reclaimInstallment(dateString:string) {

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let date = ad.dateTimeFormatter.dateFromString( dateString.substring(0, 10) + " 08:00:00" );        
        ad.webService.duplicateInstallment( this.installment, date, null );        
    } 

    private showSEPAOptionsAlert() {

        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString( "INFO", "INFO" ), MIOLocalizeString("SEPA_OPTIONS", "SEPA OPTIONS"), MUIAlertViewStyle.Default );

        avc.addComboBoxWithConfigurationHandler( this, function(this:InstallmentCell, comboBox:MUIComboBox){            
            comboBox.addItem( "SEPA CORE", 0 );
            comboBox.addItem( "SEPA B2B", 1 );
            comboBox.selectItem(0);        
        } );

        avc.addComboBoxWithConfigurationHandler(this, function (this:InstallmentCell, comboBox: MUIComboBox) {        
            DBHelper.sharedInstance().addObserverForEntity(avc, "BankAccount", null, [MIOSortDescriptor.sortDescriptorWithKey( "info.title", true)], ["info"], function (objects:BankAccount[]) {
                comboBox.removeAllItems();
                for (let i = 0; i < objects.length; i++)
                    comboBox.addItem(objects[i].info.title, i);
            });
        });

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString( "OK", "OK" ), MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
            let type = avc.comboBoxes[0].getSelectedItem();
            let account_index = avc.comboBoxes[1].getSelectedItem();
            
            let account = DBHelper.sharedInstance().objectAtIndexForEntityObserver(avc, account_index, 'BankAccount') as BankAccount;
            
            this.generateSEPAFromInstallment( parseInt(type), account );
        } ));

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString( "CANCEL", "CANCEL" ), MUIAlertActionStyle.Cancel, null, null) );

        avc.addCompletionHandler(this, function (this:InstallmentCell) {
            // Release the observers
            DBHelper.sharedInstance().removeObserverForChangesInEntity(avc.comboBoxes[0], 'StockCustomConcept');
            DBHelper.sharedInstance().removeObserverForChangesInEntity(avc.comboBoxes[1], 'Warehouse');
        });

        AppHelper.sharedInstance().presentViewController( avc, true );
    }

    private generateSEPAFromInstallment( type:number, account:BankAccount ) {
    
        let sepa_type = "19.14";
        switch(type) {
            case 0: sepa_type = "19.14"; break; // SEPA CORE
            case 1: sepa_type = "19.44"; break; // SEPA B2B
        }

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;                
        let date_str = ad.serverDateFormatter.stringFromDate( this.installment.dueDate );

        let filename = "SEPA_" + account.info.title + "_" + date_str + ".txt";
        
        (ad.webService as any).generateSEPAFileFromInstallments( [this.installment], sepa_type, account, date_str, filename, null );
    }

    private showSplitCountAlert() {
        let avc = new MUIAlertViewController();
        avc.initWithTitle( MIOLocalizeString("SPLIT_INSTALLMENT", "SPLIT INSTALLMENT"), MIOLocalizeString("NUMBER_OF_PAYMENTS", "NUMBER OF PAYMENTS"), MUIAlertViewStyle.Default );

        avc.addComboBoxWithConfigurationHandler( this, function(this:InstallmentCell, comboBox:MUIComboBox){
            for (let i = 2; i <= 6; i++) comboBox.addItem( i.toString(), i );
            comboBox.selectItem( 2 );
        });

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString("OK", "OK"), MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
            let count = parseInt( avc.comboBoxes[0].getSelectedItem() );
            this.showSplitPartsAlert( count );
        } ));

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null ) );

        AppHelper.sharedInstance().presentViewController( avc, true );
    }

    private showSplitPartsAlert( count:number ) {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        let total = this.installment.amount;
        // Usual case is a percentual split: prefill equal parts, the last one takes the remainder.
        let part = Math.round( ( total / count ) * 100 ) / 100;
        let last = Math.round( ( total - part * ( count - 1 ) ) * 100 ) / 100;

        let avc = new MUIAlertViewController();
        avc.initWithTitle( MIOLocalizeString("SPLIT_INSTALLMENT", "SPLIT INSTALLMENT"), MIOLocalizeString("AMOUNT_AND_DUE_DATE_OF_EVERY_PAYMENT", "AMOUNT AND DUE DATE OF EVERY PAYMENT"), MUIAlertViewStyle.Default );

        for (let i = 0; i < count; i++) {
            avc.addTextFieldWithConfigurationHandler( this, function(this:InstallmentCell, textField:MUITextField){
                textField.placeholderText = MIOLocalizeString("AMOUNT", "AMOUNT") + " " + ( i + 1 );
                textField.text = ad.currencyFormatter.stringFromNumber( i == count - 1 ? last : part );
            });

            avc.addTextFieldWithConfigurationHandler( this, function(this:InstallmentCell, textField:MUITextField){
                textField.placeholderText = MIOLocalizeString("DUE_DATE", "DUE DATE") + " " + ( i + 1 );
                textField.text = ad.dateFormatter.stringFromDate( this.installment.dueDate );
            });
        }

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString("SPLIT", "SPLIT"), MUIAlertActionStyle.Default, this, function(this:InstallmentCell){
            this.splitInstallment( avc, count );
        } ));

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null ) );

        AppHelper.sharedInstance().presentViewController( avc, true );
    }

    private splitInstallment( avc:MUIAlertViewController, count:number ) {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        let parts = [];
        let sum = 0;
        for (let i = 0; i < count; i++) {
            let amount = ad.currencyFormatter.numberFromString( avc.textFields[i * 2].text.trim() );
            if (amount == null || amount == 0) {
                AppHelper.showErrorMessage( null, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString("CHECK_THE_AMOUNTS", "CHECK THE AMOUNTS") );
                return;
            }
            sum = Math.round( ( sum + amount ) * 100 ) / 100;

            let part = { "amount": amount };
            let date = ad.dateFormatter.dateFromString( avc.textFields[i * 2 + 1].text.trim() );
            if (date != null) part["dueDate"] = ad.serverDateFormatter.stringFromDate( date );

            parts.push( part );
        }

        if (sum != this.installment.amount) {
            AppHelper.showErrorMessage( null, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString("THE_AMOUNTS_DONT_MATCH_THE_INSTALLMENT", "THE AMOUNTS DON'T MATCH THE INSTALLMENT") );
            return;
        }

        ad.webService.splitInstallment( this.installment, parts, (json:any, error:string) => {
            if (error != null) {
                AppHelper.showErrorMessage( null, MIOLocalizeString("ERROR", "ERROR"), error );
                return;
            }

            // Pull the new installments and the voided original into the local cache
            let ids = ( json["ids"] != null ? json["ids"] : [] ).concat( [ this.installment.identifier ] );
            for (let id of ids) {
                DBHelper.queryObjectWithCompletion( "LegalEntityPaymentInstallment", null, MIOPredicate.predicateWithFormat( "identifier == " + id ), [], this, function(this:InstallmentCell, object:PaymentInstallment){
                    if (object === this.installment) this.updatePaymentStatus();
                });
            }

            AppHelper.showInfoMessage( null, MIOLocalizeString("INFO", "INFO"), MIOLocalizeString("THE_OPERATION_WAS_SUCCESSFULL", "THE OPERATION WAS SUCCESSFULL") );
        });
    }

    /// Opens the per-transaction reconcile view of the installment's session:
    /// the venue lines against the registered rows, to find and operate on
    /// the wrong transaction behind a held payout. The list presents it, to
    /// follow the issues reviewed there.
    private showSessionTransactions() {
        let session_installment = InstallmentCell.sessionInstallmentOf( this.installment );
        if (session_installment == null) return;
        this.delegate?.installmentCellDidRequestReview( this, session_installment );
    }

    private recheckSession() {
        if (!( this.installment instanceof AdyenProxyPaymentInstallment )) return;

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        ad.webService.processSessionPayout( this.installment.sessionID, (data:any, error:string) => {
            if (error != null) {
                AppHelper.showErrorMessage( null, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString("SOMETHING_WENT_WRONG", "SOMETHING WENT WRONG") );
                return;
            }
            let result = data?.["data"]?.["result"];
            let message = result == "generated" ? MIOLocalizeString("SESSION_RECONCILED", "THE SESSION RECONCILED AND THE PAYOUT WAS GENERATED")
                                                : MIOLocalizeString("SESSION_STILL_MISMATCHED", "THE SESSION STILL DOES NOT RECONCILE");
            AppHelper.showInfoMessage( null, MIOLocalizeString("INFO", "INFO"), message );
            let installment = this.installment as AdyenProxyPaymentInstallment;
            DBHelper.queryObjectWithCompletion( "LegalEntityPaymentInstallment", null, MIOPredicate.predicateWithFormat( "identifier == " + installment.identifier ), [], this, function(this:InstallmentCell, object:PaymentInstallment){
                if (object === this.installment) this.updatePaymentStatus();
                this.delegate?.installmentCellDidChangeSession( this, installment.sessionID );
            });
        });
    }

    private doPayment() {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;        
        ad.webService.transferFunds( this.installment, (data:any, error:string) => {
            if (error == null) {
                AppHelper.showInfoMessage(null, MIOLocalizeString("INFO", "INFO"), MIOLocalizeString("THE_OPERATION_WAS_SUCCESSFULL", "THE OPERATION WAS SUCCESSFULL"));                
                DBHelper.queryObjectWithCompletion( "PaymentInstallment", null, MIOPredicate.predicateWithFormat( "identifier == " + this.installment.identifier), [], this, function(object:PaymentInstallment){
                    this.updatePaymentStatus();
                });                
            }
            else {
                AppHelper.showErrorMessage(null, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString("SOMETHING_WENT_WRONG", "SOMETHING WENT WRONG"));
            }
        } );
    }
}